/**
 * Pure helpers extracted from <BulkCardUploadModal /> so they can be
 * unit-tested without React DOM. These are the FC-E-critical eligibility
 * and count derivation rules; keeping them here makes the modal itself
 * a thin state-machine wrapper over well-tested logic.
 */

/**
 * FC-E Create-button eligibility rule (T4 / T5 / T6 / T7):
 *   Enabled iff
 *     preview.summary.can_commit === true
 *     AND preview.summary.total_rows > 0
 *     AND preview.summary.invalid_rows === 0
 *     AND a File is currently selected
 *     AND that File is IDENTICALLY the object that produced the current
 *         preview (===)
 *     AND we are not mid-commit
 */
export function computeCanCommit({ preview, file, previewedFile, phase }) {
  const s = preview && preview.summary;
  return Boolean(
    s
    && s.can_commit === true
    && s.total_rows > 0
    && s.invalid_rows === 0
    && file
    && file === previewedFile
    && phase !== "committing",
  );
}

/**
 * Confirmation-dialog network counts (T10): sum only the VALID rows
 * whose network was successfully canonicalized. Never introduces
 * ta_petro / Country / Esso USD/GAL keys — the parser rejects them
 * on the backend so they never appear on a valid row.
 */
export function computeNetworkCounts(preview) {
  const counts = {};
  const rows = preview && preview.rows;
  if (!rows) return counts;
  for (const r of rows) {
    if (r.valid && r.network) {
      counts[r.network] = (counts[r.network] || 0) + 1;
    }
  }
  return counts;
}

/**
 * FC-E response classifier — chosen banner tone + message for a failed
 * commit. Pure function so its logic can be regression-tested without a
 * network stack.
 *
 * Input `err` is the axios-style error object.
 */
export function classifyCommitError(err) {
  const status = err && err.response && err.response.status;
  const detail = err && err.response && err.response.data && err.response.data.detail;
  const code = detail && typeof detail === "object" ? detail.code : null;

  if (status === 422 && code === "BULK_COMMIT_REJECTED") {
    return {
      kind: "rejected",
      tone: "warn",
      text: "Validation changed since preview. No cards were created. Review the highlighted rows and preview again.",
      updatedPreview: {
        summary: detail.summary,
        rows: detail.rows,
        customer_id: detail.customer_id,
      },
    };
  }
  if (status === 409 && code === "BULK_COMMIT_RACE_ABORT") {
    return {
      kind: "race",
      tone: "warn",
      text: "A conflicting card was created after this preview. No cards were created. Run Preview again.",
    };
  }
  if (status === 500 && code === "BULK_COMMIT_INSERT_FAILED") {
    return {
      kind: "rolled_back",
      tone: "err",
      text: "Bulk creation failed. The attempted cards were rolled back and no cards from this upload were created.",
    };
  }
  return {
    kind: "critical",
    tone: "critical",
    text: "Critical bulk-card creation error. Do not retry this upload yet. Contact platform operations to verify whether any cards were created.",
    debugCode: status || "unknown",
  };
}
